(ns pfo.label.component
  (:require
   [pfo.controller :as controller :refer [subscribe]]
   [reagent.core :as reagent :refer [atom track!]]
   ["react-select/creatable" :default CreatableSelect]
   ["react-select" :default Select]
   [pfo.util :as util]
   [pfo.widget.autocomplete :refer [react-select-styles]]
   [pfo.api :as api :refer [process-error]]
   [promesa.core :as p]))

(def delete-label  {:id "delete" :name "Delete labels"})
(def unchanged-label {:id "unchanged" :name "Keep unchanged"})

(defn label-chooser [options family-id value-atom]
  (let [allow-create-new (:allow-create-new options)
        labels-atom (or (:labels-atom options) (subscribe :label-list))
        label-list (track! #(:results @labels-atom))
        labels-format (fn [labels]
                        (mapv
                         (fn [label]
                           {:label (:name label)
                            :value (:id label)
                            :new (boolean (:new label))})
                         labels))
        keep-unchanged (:keep-unchanged options)
        init-keep-unchanged (fn []
                             (when (and keep-unchanged (nil? @value-atom))
                               (reset! value-atom unchanged-label)))]
    (init-keep-unchanged)
    (fn [options family-id _]
      (let [filtered-labels (if-let [filter-pred (:filter-pred options)]
                              (filter filter-pred @label-list)
                              @label-list)
            labels (if keep-unchanged
                     (cons  delete-label filtered-labels)
                     filtered-labels)]
        [:> (if allow-create-new CreatableSelect Select)
         (merge
          {:options (->> (if (and (:allow-empty options) (not-empty (str (:id @value-atom))))
                           (merge (labels-format labels) (if keep-unchanged
                                                           {:label "Keep unchanged" :value "unchanged"}
                                                           {:label "Not selected" :value nil}))
                           (labels-format labels)))
           :placeholder (cond (:disabled options) ""
                              keep-unchanged  "Keep unchanged"
                              :else  "Choose")
           :isDisabled  (or (not (seq options)) (:disabled options))
           :value       (filter
                         (fn [label] (= (:id @value-atom) (:value label)))
                         (labels-format labels))

           :on-change   (fn [e]
                          (let [ev (js->clj e :keywordize-keys true)
                                is-new (:__isNew__ ev)
                                value (:value ev)]
                            (if is-new
                              (do
                                (reset! value-atom {:id value :name value :new true})
                                (some-> (:create-cb options) (apply [{:name value :id value :new true}]))
                                (swap! labels-atom update :results conj {:name value :id value :new true}))
                              (do
                                (reset! value-atom {:id value :new false})
                                (some-> (:change-cb options) (apply [(merge {:id value :new (:new ev)}
                                                                            (when (:new ev)
                                                                              {:name value}))]))))))}
          (:primary react-select-styles))] ))))

(defn label-chooser-multi [options family-id value-atom]
  (let [allow-create-new (:allow-create-new options)
        labels-atom (or (:labels-atom options) (subscribe :label-list))
        label-list (track! #(:results @labels-atom))
        labels-format (fn [labels]
                        (mapv
                         (fn [label]
                           {:label (:name label)
                            :value (:id label)
                            :new (boolean (:new label))})
                         labels))
        keep-unchanged (:keep-unchanged options)
        init-keep-unchanged (fn []
                             (when (and keep-unchanged (nil? @value-atom))
                               (reset! value-atom [unchanged-label])))]
    (init-keep-unchanged)
    (fn [options family-id _]
      (let [filtered-labels (if-let [filter-pred (:filter-pred options)]
                              (filter filter-pred @label-list)
                              @label-list)
            labels (if keep-unchanged
                     (cons  delete-label filtered-labels)
                     filtered-labels)
            value-id-set (set (map :id @value-atom))]
        [:> (if allow-create-new CreatableSelect Select)
         (merge
          {:options (->> (if (and (:allow-empty options) (not-empty (str (:id @value-atom))))
                           (merge (labels-format labels) (if keep-unchanged
                                                           {:label "Keep unchanged" :value "unchanged"}
                                                           {:label "Not selected" :value nil}))
                           (labels-format labels)))
           :isMulti true
           :placeholder (cond (:disabled options) ""
                              keep-unchanged  "Keep unchanged"
                              :else  "Choose")
           :isDisabled  (or (not (seq options)) (:disabled options))
           :value       (util/print-and-return "VALUE" (filter
                         (fn [label]
                           (contains? value-id-set (:value label)))
                         (labels-format labels)))

           :on-change   (fn [e]
                          (let [ev (js->clj e :keywordize-keys true)
                                new-labels (filter #(:__isNew__ %) ev)]
                            (reset! value-atom (mapv (fn [lb]
                                                       (if (:__isNew__ lb)
                                                         {:id (:value lb) :name (:value lb) :new true}
                                                         {:id (:value lb) :new false})) ev))
                            (swap! labels-atom update :results concat (map (fn [lb] {:name (:value lb) :id (:value lb) :new true}) new-labels) )))}
          (:primary react-select-styles))] ))))



(defn label [& names]
  [:<>
   (doall
     (for [name names]
       [:div.c-label--container {:key name}
        [:div.c-label--text name]]))])

(defn label-inline-chooser [options labelable edit-label-atom]
  (let [label (atom (first (:labels labelable)))
        errors (atom nil)     
        set-label-cb (:set-label-cb options)]
    (fn []
      [:div
       [label-chooser (merge options
                        {:allow-empty false :allow-create-new true}) (:family-id options) label]
       [:div.u-margin-top--xs
        [:button.c-round-button.u-margin-left--xs  {:on-click
                                                    (util/handler
                                                     (fn []
                                                       (reset! edit-label-atom nil)))
                                                    :style {:font-size "1.1em"}}
         "⟲"]
        [:button.c-round-button.u-margin-left--xs {:on-click
                                                   (util/handler (fn []
                                                                   (set-label-cb labelable label)))}
         "✓"]
        (when-not (:new options)
          [:button.c-round-button.u-margin-left--xs {:on-click
                                                     (util/handler
                                                      (fn []
                                                        (reset! label nil)
                                                        (util/handler (set-label-cb labelable label))))}
           "×"])]
       [:p [:span.error (:label_id @errors)]]
       [:p [:span.error (:label_name_new @errors)]]])))

(defn filter-by-label [options]
  (let [labels-atom (subscribe :label-list)
        label-list (track! #(:results @labels-atom))
        label-ids (or (:label-ids-atom options) (atom nil))
        callback (:load-cb options)        
        merge-duplicates (fn [labels]
                           (map
                            (fn [[label-name grouped-labels]] {:label label-name :value (mapv :value grouped-labels)})
                            (group-by :label labels)))
        
        labels-format (fn [labels]
                        (for [label labels]
                          {:label (:name label) :value (:id label)}))
        labels (atom (merge-duplicates (labels-format @label-list)))
        label-ids-update (fn []
                           (reset! label-ids (mapv (fn [ids]
                                                     (vec (flatten (mapv :value 
                                                                         (filterv (fn [label]
                                                                                    (some #(contains? (set (:value label)) %) ids))
                                                                                  @labels)))))
                                                   @label-ids)))]
    
    (add-watch label-list :label-list (fn [_ _ _ new-label-list]
                                        (reset! labels (merge-duplicates (labels-format new-label-list)))
                                        (label-ids-update)))

    (fn []
      [:div.filter-item.c-form--edit
       [:h3.u-margin-bottom.u-padding-top.u-cursor--pointer {:class (if (:line-class options) (:line-class options) "u-border-top")}
        (if (:title options)
          (:title options)
          "Filter by labels")]
       [:div.u-flex
        [:div.u-width--full
         [:>
          Select
          (merge
           {:options     (filter
                          (fn [label]
                            (not (some #{(:value label)} @label-ids)))
                          @labels)
            :value (filter
                    (fn [label]
                      (some #{(:value label)} @label-ids))
                    @labels)
            :isMulti     true
            :class       :to
            :placeholder "Label"
            :on-change   (fn [x]
                           (reset! label-ids
                                   (mapv (fn [entity] (-> entity :value)) (js->clj x :keywordize-keys true)))
                           (when callback
                             (callback)))}
           (get react-select-styles (:select-styles options) (:primary react-select-styles)))]]]])))
